/* eslint-disable react-refresh/only-export-components -- Auth module intentionally exports both components and utilities */
import { Amplify } from 'aws-amplify';
import {
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  rememberDevice,
  resendSignUpCode,
  setUpTOTP,
  signIn,
  signOut,
  signUp,
  updateMFAPreference,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import {
  generatePath,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';
import { toast } from 'sonner';

import { AuthenticatedRoute, NonAuthenticatedRoute } from '@/app/constant';
import TransitionScreen from '@/components/TransitionScreen';
import { Button } from '@/components/ui/button';
import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { useGlobalState } from '@/state/global.state';
import {
  AuthContextType,
  AuthState,
  BackendStack,
  UserType,
  WhoamiResponse,
} from '@/types';
import { decode, encode } from '@/utils/encrypt.util';
import { validateRedirectUrl } from '@/utils/redirect.util';
import { Regex } from '@/utils/regex.util';
import { appStorage } from '@/utils/storage/appStorage.util';
import {
  clearCurrentTenant,
  CURRENT_TENANT_KEY,
  dispatchTenantChange,
  removeTenantScopedValue,
  setCurrentTenant,
} from '@/utils/storage/tenantScoping.util';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Derive the `me` identity from a whoami response.
 * SSO users are identified by their tenant email; all others by their principal email.
 */
function deriveMe(whoami: WhoamiResponse): string {
  return whoami.principal.access_type === 'sso'
    ? (whoami.tenant?.name ?? '')
    : whoami.principal.name;
}

const defaultStack: BackendStack = {
  backend: import.meta.env.VITE_BACKEND || 'chariot',
  api:
    import.meta.env.VITE_API_URL ||
    'https://d0qcl2e18h.execute-api.us-east-2.amazonaws.com/chariot',
  clientId: import.meta.env.VITE_CLIENT_ID || '795dnnr45so7m17cppta0b295o',
  userPoolId: import.meta.env.VITE_USER_POOL_ID || 'us-east-2_BJ6QHVG2L',
};

export const emptyAuth: AuthState = {
  ...defaultStack,
  me: '',
  sub: '',
  principal: '',
  isSignedIn: false,
  ssoDomain: '',
  whoami: undefined,
};

const DEMO_USER_EMAILS = new Set<string>([
  'demo@praetorian.com',
  'chariot+chariot_demoq1crrfeqob1n9d0j15gmbdd0wr3zj9lb@praetorian.com',
]);

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { setLastVisitedUserId } = useGlobalState();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string>('');
  const [searchParams, setSearchParams] = useSearchParams();
  const emailFromQuery = searchParams.get('email');

  const [credentials, setCredentials] = useState<{
    username: string;
    password: string;
  }>({ username: emailFromQuery || '', password: '' });
  const [auth, setAuth] = useStorage<AuthState>(
    { key: StorageKey.AUTH },
    emptyAuth
  );
  const [loginCred, setLoginCred] = useStorage<{
    username: string;
    password: string;
  }>(
    {
      key: 'chariot.loginCred',
    },
    {
      username: '',
      password: '',
    }
  );

  const [signupStepIndex, setSignupStepIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isTokenFetching, setIsTokenFetching] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [totpSecret, setTotpSecret] = useState<string>('');
  const [totpUri, setTotpUri] = useState<string>('');
  const [pendingMfaSignIn, setPendingMfaSignIn] = useStorage<boolean>(
    { key: 'pendingMfaSignIn' },
    false
  );
  const [showMFACodeModal, setShowMFACodeModal] = useState<boolean>(false);

  // Create axios instance at component level (hooks must be called at top level)
  const axiosInstance = useAxios({ doNotImpersonate: true });

  // Fetch whoami data from the backend
  async function fetchWhoami(account?: string): Promise<WhoamiResponse> {
    const token = await getToken();
    const headers: Record<string, string> = {
      Authorization: token ? `Bearer ${token}` : '',
    };
    if (account) {
      headers['account'] = account;
    }

    const { data } = await axiosInstance.get<WhoamiResponse>('/whoami', {
      baseURL: auth.api,
      headers,
    });
    return data;
  }

  // Retry whoami once on failure (handles transient network errors)
  async function fetchWhoamiWithRetry(
    account?: string
  ): Promise<WhoamiResponse> {
    try {
      return await fetchWhoami(account);
    } catch {
      // Single retry, minimal delay
      await new Promise(resolve => setTimeout(resolve, 300));
      return await fetchWhoami(account);
    }
  }

  // Stabilize the onComplete callback to prevent unnecessary re-renders
  const handleTransitionComplete = useCallback(() => {
    setIsSwitching(false);
  }, []);

  /**
   * Fetch whoami and update auth state. Shared by fetchAuthInfo,
   * startImpersonation, and stopImpersonation.
   *
   * Uses flushSync so that downstream consumers (e.g. StatsigWrapper)
   * see the updated whoami before any navigation triggers auto-capture events.
   */
  async function refreshAuth(account?: string): Promise<WhoamiResponse> {
    const whoami = await fetchWhoamiWithRetry(account);
    flushSync(() => {
      setAuth(prevAuth => ({
        ...prevAuth,
        isSignedIn: true,
        me: deriveMe(whoami),
        sub: whoami.principal.sub,
        principal: whoami.principal.name,
        ssoDomain: whoami.principal.sso_domain ?? '',
        whoami,
      }));
    });
    return whoami;
  }

  async function startImpersonation(
    memberId: string,
    route: string = AuthenticatedRoute.INSIGHTS,
    disableSwitchingAnimation: boolean = false
  ) {
    setCurrentTenant(memberId);
    dispatchTenantChange();
    if (!disableSwitchingAnimation) {
      setIsSwitching(true);
    }
    queryClient.clear();

    try {
      await refreshAuth(memberId);
    } catch {
      setIsSwitching(false);
      setAuthError(true);
      return;
    }

    navigate(
      generatePath(`:userId/${route}`, {
        userId: encode(memberId),
      })
    );
  }

  async function stopImpersonation(
    route: string = AuthenticatedRoute.INSIGHTS,
    disableSwitchingAnimation: boolean = false,
    enablePersonalTenancy: boolean = false
  ) {
    if (!isPraetorianUser && !enablePersonalTenancy) {
      return;
    }

    clearCurrentTenant();
    dispatchTenantChange();

    if (!disableSwitchingAnimation) {
      setIsSwitching(true);
    }
    queryClient.clear();

    try {
      await refreshAuth();
    } catch {
      setIsSwitching(false);
      setAuthError(true);
      return;
    }

    navigate(
      generatePath(`:userId/${route}`, {
        userId: encode(auth.me),
      })
    );
  }

  async function resendEmail(username: string) {
    await resendSignUpCode({
      username,
    });
  }

  async function setTotpSecretDetails(totpSetupDetails: {
    sharedSecret: string;
    getSetupUri: (appName: string) => URL;
  }) {
    setTotpSecret(totpSetupDetails.sharedSecret);
    const appName = 'Chariot';
    const setupUri = totpSetupDetails.getSetupUri(appName);
    setTotpUri(setupUri.toString());
  }

  async function login(username: string, password: string) {
    // SECURITY: Clear any stale tenant key from previous session
    // Prevents cross-tenant data leakage if browser was closed without logout
    clearCurrentTenant();

    try {
      if (username && password) {
        setIsLoading(true);
        const { isSignedIn, nextStep } = await signIn({
          username,
          password,
        });

        if (isSignedIn) {
          const session = await fetchAuthSession();
          const mfaDeadline =
            session.tokens?.idToken?.payload?.['custom:mfa_required_by'];

          if (mfaDeadline) {
            setSignupStepIndex(2);
            navigate(NonAuthenticatedRoute.SIGNUP);
            const totpSetupDetails = await setUpTOTP();
            setTotpSecretDetails(totpSetupDetails);
          } else {
            goToDashboard();
          }
        } else {
          if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
            setSignupStepIndex(1);
            resendEmail(username);
            navigate(NonAuthenticatedRoute.SIGNUP);
          } else if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
            setShowMFACodeModal(true);
          }
        }
      }
    } catch (error) {
      if (error instanceof Error && error.message) {
        setError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function signup(username = '', password = '', gotoNext = () => {}) {
    try {
      setIsLoading(true);
      const { isSignUpComplete, nextStep } = await signUp({
        username,
        password,
        options: {
          userAttributes: {
            'custom:query': window.location.search,
            updated_at: Date.now().toString(),
          },
        },
      });
      if (!isSignUpComplete && nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        if (gotoNext) {
          gotoNext();
        }
      }
    } catch (error) {
      if (error instanceof Error && error.message) {
        if (error.message === 'User already exists') {
          login(username, password);
        } else {
          setError(error.message);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function confirmOTP(username = '', password = '', otp: string = '') {
    try {
      setIsLoading(true);
      const response = await confirmSignUp({
        username,
        confirmationCode: otp,
      });
      if (response.isSignUpComplete) {
        await login(username, password);
      }
    } catch (error) {
      if (error instanceof Error && error.message) {
        toast.error('Failed to confirm OTP');
        console.error(error);
      }
      setIsLoading(false);
    }
  }

  // fetchAuthInfo calls /whoami to get the user's identity, role, and entitlements.
  // It takes into account whether we should be impersonating into a different tenant,
  // which can come from the URL path.
  // Called once on login and once on OAuth redirect completion.
  async function fetchAuthInfo() {
    setIsTokenFetching(true);
    const session = await fetchAuthSession();

    if (session.tokens) {
      try {
        // Determine which tenant to fetch whoami for. Priority:
        // 1. URL path (user pasted a link for a specific tenant)
        // 2. Storage (resuming a previous impersonation session)
        const impersonatedTenant =
          tenantFromUrl() || appStorage.getItem<string>(CURRENT_TENANT_KEY);
        setAuthError(false);
        await refreshAuth(impersonatedTenant || undefined);
      } catch {
        // Retry already attempted inside fetchWhoamiWithRetry — show error page
        setAuthError(true);
      }
    }
    setIsTokenFetching(false);
  }

  async function logout() {
    try {
      setPendingMfaSignIn(false);
      await signOut();
    } catch {
      // Do nothing
    }

    queryClient.clear();
    setAuthError(false);
    setAuth(emptyAuth);
    // Clear AI assistant session so next login starts fresh
    removeTenantScopedValue('ai-assistant.lastActiveConversationId');
    // Clear impersonation tenant to prevent cross-tenant data leakage on next login
    clearCurrentTenant();
    // Clear the graph explorer cluster selection on logout
    appStorage.removeItem('graphExplorer.clusterByEntity');
    // Clear default filter session flags for all tenants (including impersonation
    // residue) so next login re-applies defaults.
    try {
      const SESSION_FILTER_KEY_SUFFIXES = [
        ':vuln-default-demonstrated-filter',
        ':seed-default-active-filter',
        ':asset-default-active-filter',
        ':agent-default-active-filter',
      ];
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (
          key &&
          SESSION_FILTER_KEY_SUFFIXES.some(suffix => key.endsWith(suffix))
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(k => sessionStorage.removeItem(k));
    } catch {
      // sessionStorage unavailable (e.g., Safari private browsing)
    }
    navigate(NonAuthenticatedRoute.LOGIN);
  }

  function setCustomStack(
    backendStack: BackendStack = defaultStack,
    loginCred: { username: string; password: string } = {
      username: '',
      password: '',
    }
  ) {
    setLoginCred(loginCred);

    setAuth(prevAuth => ({
      ...prevAuth,
      ...backendStack,
    }));
    window.location.reload();
  }

  async function getToken() {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString() ?? '';

    if (!token) {
      if (auth.isSignedIn) {
        toast.error('Init token missing');
        logout();
      }
      return '';
    }

    return token;
  }

  function reset() {
    setCredentials({ username: '', password: '' });
    setSignupStepIndex(0);
    setTotpSecret('');
    setTotpUri('');
  }

  async function goToDashboard() {
    reset();
    setPendingMfaSignIn(false);
    await fetchAuthInfo();
    // Email/password: CheckNoAuth reads ?redirect= URL param (set by CheckAuth before login)
    // OAuth/SSO: Hub.listen handles 'signInWithRedirect' event and uses sessionStorage.oauth_redirect_url
    // Do NOT navigate here - the redirect mechanisms above handle navigation after auth completes
  }

  const verifyMFASetupSignup = async (
    code: string,
    navigateToDashboard?: boolean
  ): Promise<void> => {
    try {
      await verifyTOTPSetup({ code });

      await updateMFAPreference({
        totp: 'PREFERRED',
      });

      if (navigateToDashboard) {
        goToDashboard();
      }
    } catch (error) {
      console.error('Error verifying MFA setup:', error);
      // Error handling is already done in verifyMFASetupLogin
      throw error;
    }
  };

  async function verifyMFASetupLogin(
    code: string,
    rememberDeviceChecked?: boolean
  ) {
    try {
      setIsLoading(true);

      if (rememberDeviceChecked) {
        await rememberDevice();
      }

      const result = await confirmSignIn({ challengeResponse: code });

      if (result.isSignedIn) {
        // The signedIn event will handle navigation
        goToDashboard();
      }

      return result;
    } catch (error) {
      console.error('Error verifying TOTP code:', error);
      if (error instanceof Error) {
        if (error.message.includes('session is expired')) {
          toast.error('Session expired. Please sign in again.');
          setTotpSecret('');
          setTotpUri('');
          navigate(NonAuthenticatedRoute.LOGIN);
        } else {
          toast.error('Invalid verification code. Please try again.');
        }
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  }

  // Parse tenant from URL path (e.g., /:base64UserId/assets).
  // Returns the decoded tenant email, or '' if no tenant segment in the URL
  // or if the segment is malformed (invalid base64).
  function tenantFromUrl(): string {
    const parts = location.pathname.split('/');
    const segment = parts.length > 2 ? parts[1] : '';
    if (!segment) return '';
    return decode(decodeURIComponent(segment), '');
  }

  useEffect(() => {
    const stopListen = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'tokenRefresh_failure': {
          // Skip logout if step-up re-authentication is in progress
          if (sessionStorage.getItem('step_up_reauth')) {
            break;
          }
          toast.error('Session expired, Please login again.');
          logout();
          break;
        }
        case 'signInWithRedirect': {
          // SECURITY: Clear any stale tenant key from previous session
          // Prevents cross-tenant data leakage if browser was closed without logout
          clearCurrentTenant();

          // OAuth flow completed - check if there's a redirect URL stored before OAuth started
          const oauthRedirect = sessionStorage.getItem('oauth_redirect_url');
          sessionStorage.removeItem('oauth_redirect_url');

          // SECURITY: Validate redirect to prevent open redirect attacks (CWE-601)
          // Must validate even though storage point also validates (defense in depth)
          const destination = validateRedirectUrl(oauthRedirect);

          await fetchAuthInfo();
          navigate(destination);
          break;
        }
        case 'signInWithRedirect_failure': {
          console.error('OAuth sign-in failed:', payload);
          toast.error('Authentication failed. Please try again.');
          break;
        }
        default: {
          // Combined case for all other events and removed console.log to avoid spamming the console.
          break;
        }
      }
    });

    return () => {
      stopListen();
    };
    // Note: Using empty deps because the Hub listener handles OAuth redirect via
    // sessionStorage.oauth_redirect_url. No external state dependencies needed.
  }, []);

  useEffect(() => {
    if (emailFromQuery) {
      searchParams.delete('email');
      setSearchParams(searchParams);
    }
    if (loginCred.username && loginCred.password) {
      setIsTokenFetching(false);
      login(loginCred.username, loginCred.password);
      setLoginCred({ username: '', password: '' });
    } else {
      fetchAuthInfo();
    }
  }, []);

  const userId = tenantFromUrl() || auth.me;
  const base64UserId = location.pathname.split('/')[1] ?? '';

  const isImpersonating = auth.whoami?.is_assuming ?? false;

  useEffect(() => {
    if (base64UserId && base64UserId !== 'app') {
      setLastVisitedUserId(userId !== auth.me ? userId : '');
    }
  }, [userId]);

  // Check if we're on a non-authenticated route (login, signup, etc.)
  const isNonAuthenticatedRoute = Object.values(NonAuthenticatedRoute).some(
    route => location.pathname === route || location.pathname.startsWith(route)
  );

  if (isTokenFetching && !isNonAuthenticatedRoute) {
    // Show a loading screen instead of blank while checking for existing session.
    // This improves UX for unauthenticated users who would otherwise see a blank screen
    // while Cognito validates that there's no active session.
    // Skip this for non-authenticated routes (login, signup) to avoid flash.
    return (
      <TransitionScreen
        message="Checking authentication..."
        onComplete={() => {}}
      />
    );
  }

  if (authError) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p className="text-chariot-text-primary text-lg font-semibold">
          Unable to retrieve authentication information
        </p>
        <p className="text-chariot-text-secondary text-sm">
          Please try again or contact us at support@praetorian.com if the
          problem persists
        </p>
        <div className="flex gap-3">
          <Button
            onClick={() => {
              setAuthError(false);
              setIsTokenFetching(true);
              fetchAuthInfo();
            }}
          >
            Try Again
          </Button>
          <Button variant="outline" onClick={logout}>
            Log Out
          </Button>
        </div>
      </div>
    );
  }

  // A user is considered a Praetorian user if their email ends with @praetorian.com,
  // and they are either not using SSO or are using Google SSO (since Google SSO provides the actual user email).
  // For other SSO providers, the email may be inherited from the parent account and not reflect the true user.
  const isPraetorianUser =
    auth?.me?.endsWith('@praetorian.com') &&
    (!auth?.ssoDomain || auth?.ssoDomain === 'Google');

  // Compute userType as a discriminated union for explicit authorization logic
  // This provides better type safety and more explicit intent compared to boolean flags
  const userType: UserType =
    auth?.me?.endsWith('@praetorian.com') &&
    (!auth?.ssoDomain || auth?.ssoDomain === 'Google')
      ? 'praetorian'
      : auth?.ssoDomain && auth?.ssoDomain !== 'Google'
        ? 'sso-customer'
        : 'customer';

  const isDemoOrg =
    userId ===
    'chariot+chariot_demoq1crrfeqob1n9d0j15gmbdd0wr3zj9lb@praetorian.com';

  const isDemoUser = !!auth?.me && DEMO_USER_EMAILS.has(auth.me.toLowerCase());

  return (
    <AuthContext.Provider
      value={{
        ...auth,
        getToken,
        error,
        setError,
        confirmOTP,
        isImpersonating,
        friend: isImpersonating ? userId : '',
        userIdFromUrl: userId,
        isLoading,
        login,
        logout,
        signup,
        startImpersonation,
        stopImpersonation,
        setCustomStack,
        signupStepIndex,
        setSignupStepIndex,
        credentials,
        setCredentials,
        isPraetorianUser,
        userType,
        isDemoOrg,
        isDemoUser,
        totpSecret,
        totpUri,
        showMFACodeModal,
        setShowMFACodeModal,
        setTotpSecretDetails,
        verifyMFASetupSignup,
        setIsLoading,
        verifyMFASetupLogin,
        setPendingMfaSignIn,
        pendingMfaSignIn,
      }}
    >
      {children}
      {isSwitching && (
        <TransitionScreen
          message="Switching accounts, please wait..."
          onComplete={handleTransitionComplete}
        />
      )}
    </AuthContext.Provider>
  );
};

// Hook to use auth context in any functional component
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthProvider;

function initAmplify(stack: BackendStack = defaultStack) {
  const { clientId, userPoolId, api, backend } = stack;

  const region = Regex.AWS_REGION_REGEX.exec(api)?.[1] ?? 'us-east-2';

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolClientId: clientId,
        userPoolId,
        loginWith: {
          oauth: {
            domain: `praetorian-${backend}.auth.${region}.amazoncognito.com`,
            scopes: ['email', 'openid'],
            redirectSignIn: [
              'https://guard.praetorian.com/hello',
              'https://8472.app.guard.praetorian.com/hello',
              'https://localhost:3000/hello',
              'https://uat.app.staging.guard.praetorian.com/hello',
            ],
            redirectSignOut: [
              'https://guard.praetorian.com/goodbye',
              'https://8472.app.guard.praetorian.com/goodbye',
              'https://localhost:3000/goodbye',
              'https://uat.app.staging.guard.praetorian.com/goodbye',
            ],
            responseType: 'code',
          },
        },
      },
    },
    API: {
      REST: {
        [backend]: {
          endpoint: api,
          region,
        },
      },
    },
  });
}

initAmplify(appStorage.getItem<AuthState>(StorageKey.AUTH));
